import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { PieChartProps, PieChartType } from '@types';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const PieChart = ({
  label,
  value,
  colors,
  showDataLabel = true,
  chartType = PieChartType.PIE
}: PieChartProps) => {
  const data = {
    labels: label,
    datasets: [
      {
        data: value,
        backgroundColor: colors,
        borderWidth: 0,
        hoverOffset: 10
      }
    ]
  };

  const externalTooltipHandler = (context: any) => {
    const { chart, tooltip } = context;

    let tooltipEl = chart.canvas.parentNode.querySelector(
      '.chartjs-custom-tooltip'
    );

    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.className = 'chartjs-custom-tooltip';

      Object.assign(tooltipEl.style, {
        position: 'absolute',
        background: '#fff',
        color: '#000',
        border: '1px solid #000',
        borderRadius: '6px',
        padding: '8px 10px',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        zIndex: '100',
        fontSize: '12px',
        lineHeight: '1.4',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
      });

      chart.canvas.parentNode.appendChild(tooltipEl);
    }

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = '0';
      return;
    }

    if (tooltip.dataPoints?.length) {
      const dataPoint = tooltip.dataPoints[0];

      const formattedValue = Number(dataPoint.raw)
        .toFixed(1)
        .replace(/\.?0+$/, '');

      tooltipEl.innerHTML = `
  <div style="font-weight: 500;">
    ${dataPoint.label}: ${formattedValue}%
  </div>
`;
    }

    const chartRect = chart.canvas.getBoundingClientRect();

    const tooltipX = chartRect.left + tooltip.caretX;
    const tooltipY = chartRect.top + tooltip.caretY;

    const centerX = chartRect.left + chartRect.width / 2;
    const centerY = chartRect.top + chartRect.height / 2;

    const dx = tooltipX - centerX;
    const dy = tooltipY - centerY;

    const distance = Math.sqrt(dx * dx + dy * dy);

    const offset = 30;

    const finalX = tooltipX + (distance ? (dx / distance) * offset : 0);

    const finalY = tooltipY + (distance ? (dy / distance) * offset : 0);

    tooltipEl.style.position = 'fixed';
    tooltipEl.style.left = `${finalX}px`;
    tooltipEl.style.top = `${finalY}px`;
    tooltipEl.style.transform = 'translate(-50%, -50%)';
    tooltipEl.style.opacity = '1';
  };

  const options = {
    layout: {
      padding: 5
    },
    responsive: true,
    maintainAspectRatio: false,
    cutout: chartType === PieChartType.DONUT ? '50%' : '0%',

    plugins: {
      legend: {
        display: false
      },

      tooltip: {
        enabled: false,
        external: externalTooltipHandler
      },

      datalabels: {
        display: showDataLabel,
        anchor: 'center' as const,
        align: 'bottom' as const,
        textAlign: 'center' as const,
        font: {
          size: 20
        },
        labels: {
          value: {
            color: 'black'
          }
        },
        formatter(val: any) {
          return `${val}`;
        }
      }
    }
  };

  return <Pie data={data} options={options} />;
};

export default PieChart;
